import filter from 'lodash/filter';
import { gql, useQuery } from '@apollo/client';
import { useMutation } from '../hooks';
import {
  userFieldDefinitionFields as contactFormUserFieldDefinitionFields,
  contactFormSettingsQuery,
} from '../pages/ContactForm/graphql';

const userFieldDefinitionFields = gql`
  fragment UserFieldDefinitionFields on UserFieldDefinition {
    ...ContactFormUserFieldDefinitionFields
  }
  ${contactFormUserFieldDefinitionFields}
`;

const userFieldDefinitionsQuery = gql`
  query UserFieldDefinitions($entityType: ContactFormEntity!, $entityId: ID!) {
    userFieldDefinitions(entityType: $entityType, entityId: $entityId) {
      id
      ...UserFieldDefinitionFields
    }
  }
  ${userFieldDefinitionFields}
`;

const createMutation = gql`
  mutation CreateUserFieldDefinition($input: CreateUserFieldDefinitionInput!) {
    createUserFieldDefinition(input: $input) {
      userFieldDefinition {
        id
        ...UserFieldDefinitionFields
      }
      errors {
        message
      }
    }
  }
  ${userFieldDefinitionFields}
`;

const updateMutation = gql`
  mutation UpdateUserFieldDefinition($input: UpdateUserFieldDefinitionInput!) {
    updateUserFieldDefinition(input: $input) {
      userFieldDefinition {
        id
        ...UserFieldDefinitionFields
      }
      errors {
        message
      }
    }
  }
  ${userFieldDefinitionFields}
`;

const reorderMutation = gql`
  mutation ReorderUserFieldDefinitions(
    $input: ReorderUserFieldDefinitionsInput!
  ) {
    reorderUserFieldDefinitions(input: $input) {
      userFieldDefinitions {
        id
        ...UserFieldDefinitionFields
      }
      errors {
        message
      }
    }
  }
  ${userFieldDefinitionFields}
`;

const removeMutation = gql`
  mutation RemoveUserFieldDefinition($input: RemoveUserFieldDefinitionInput!) {
    removeUserFieldDefinition(input: $input) {
      userFieldDefinition {
        id
        ...UserFieldDefinitionFields
      }
      errors {
        message
      }
    }
  }
  ${userFieldDefinitionFields}
`;

export const useUserFieldDefinitionsQuery = ({
  variables,
  skip = false,
  fetchPolicy = 'network-only',
}) => useQuery(userFieldDefinitionsQuery, { variables, skip, fetchPolicy });

export const useUpdateUserFieldDefinitionMutation = (config) =>
  useMutation(updateMutation, config);

export const useReorderUserFieldDefinitionsMutation = (config) =>
  useMutation(reorderMutation, config);

export const useCreateUserFieldDefinitionMutation = (
  { entityType, entityId },
  config
) =>
  useMutation(createMutation, {
    update: (cache, { data: { createUserFieldDefinition } }) => {
      const variables = {
        entityType,
        entityId,
      };
      const cachedData = cache.readQuery({
        query: contactFormSettingsQuery,
        variables,
      });
      if (!cachedData) return;
      if (!createUserFieldDefinition.userFieldDefinition) return;
      const userFieldDefinitions = [
        createUserFieldDefinition.userFieldDefinition,
        ...cachedData.contactFormSettings.userFieldDefinitions,
      ];
      const data = {
        contactFormSettings: {
          ...cachedData.contactFormSettings,
          userFieldDefinitions,
        },
      };

      cache.writeQuery({
        query: userFieldDefinitionsQuery,
        data: {
          userFieldDefinitions,
        },
        variables,
      });

      cache.writeQuery({ query: contactFormSettingsQuery, data, variables });
    },
    ...config,
  });

export const useRemoveUserFieldDefinitionMutation = (
  { entityType, entityId },
  config
) =>
  useMutation(removeMutation, {
    update: (cache, { data: { removeUserFieldDefinition } }) => {
      const variables = {
        entityType,
        entityId,
      };
      const cachedData = cache.readQuery({
        query: contactFormSettingsQuery,
        variables,
      });
      if (!cachedData) return;
      const userFieldDefinitions = filter(
        cachedData.contactFormSettings.userFieldDefinitions,
        (ufd) => ufd.id !== removeUserFieldDefinition.userFieldDefinition.id
      );
      const data = {
        contactFormSettings: {
          ...cachedData.contactFormSettings,
          userFieldDefinitions: [...userFieldDefinitions],
        },
      };
      cache.writeQuery({
        query: userFieldDefinitionsQuery,
        data: {
          userFieldDefinitions,
        },
        variables,
      });

      cache.writeQuery({ query: contactFormSettingsQuery, data, variables });
    },
    ...config,
  });
